import { z } from "zod";

import { filter } from "~/tools/filter";
import { identity } from "~/tools/identity";
import type { GetImpulseFormInput, ImpulseFormSwitch, ImpulseFormUnit } from "~/tools/impulse-form";
import { isPresent } from "~/tools/is-present";
import type { Partial } from "~/tools/partial";
import { prop } from "~/tools/prop";
import { sortBy } from "~/tools/sort-by";

import type { BarChartFormState } from "../../components/widget-form/bar-chart-form-state";
import { WidgetCalculation } from "../../components/widget-form/calculation/form-state";
import { WidgetColor } from "../../components/widget-form/color/form-state";
import { WidgetConnectNulls } from "../../components/widget-form/connect-nulls/form-state";
import { WidgetDecimals } from "../../components/widget-form/decimals/form-state";
import { WidgetFontSize } from "../../components/widget-form/font-size/form-state";
import type { GaugeChartFormState } from "../../components/widget-form/gauge-chart-form-state";
import { WidgetLegend } from "../../components/widget-form/legend/form-state";
import { WidgetMetricLabel } from "../../components/widget-form/metric-label/form-state";
import { WidgetRange } from "../../components/widget-form/range/form-state";
import { WidgetSparkline } from "../../components/widget-form/sparkline/form-state";
import type { StatChartFormState } from "../../components/widget-form/stat-chart-form-state";
import type { WidgetFormSync } from "../../components/widget-form/sync";
import { WidgetThresholdsMode, WidgetThresholdsStep } from "../../components/widget-form/thresholds/form-state";
import type { TimeSeriesChartFormState } from "../../components/widget-form/time-series-chart-form-state";
import { WidgetUnit } from "../../components/widget-form/unit/form-state";
import { WidgetYAxis } from "../../components/widget-form/y-axis/form-state";

import { object, partial, tuple } from "./_decoders";
import { Encoder } from "./_encoder";

const TIME_SERIES_CHART = "TimeSeriesChart";
const BAR_CHART = "BarChart";
const GAUGE_CHART = "GaugeChart";
const STAT_CHART = "StatChart";

const ALL_KINDS = [TIME_SERIES_CHART, BAR_CHART, GAUGE_CHART, STAT_CHART];

interface SettingsBranches {
	TimeSeriesChart: TimeSeriesChartFormState;
	BarChart: BarChartFormState;
	GaugeChart: GaugeChartFormState;
	StatChart: StatChartFormState;
}

type FormState = ImpulseFormSwitch<
	ImpulseFormUnit<string, ReadonlyArray<string>, keyof SettingsBranches>,
	SettingsBranches
>;

const decoder = object({
	0: z
		.number()
		.transform((index) => prop(ALL_KINDS, index))
		.catch(undefined),

	1: z.string().optional().catch(undefined),
	2: z.string().optional().catch(undefined),
	3: z.boolean().optional().catch(undefined),
	4: z.string().optional().catch(undefined),
	5: z.string().optional().catch(undefined),
	6: z.string().optional().catch(undefined),
	7: z.string().optional().catch(undefined),
	8: z.boolean().optional().catch(undefined),
	9: z.string().optional().catch(undefined),
	10: z.string().optional().catch(undefined),

	11: tuple([
		z.boolean().optional().catch(undefined),
		z.string().optional().catch(undefined),
		z.string().optional().catch(undefined),
		z.array(z.string().optional().catch(undefined)).transform(filter(isPresent)).optional().catch(undefined),
	])
		.transform(
			([enabled, position, mode, table]) =>
				partial({ enabled, position, mode, table }) satisfies undefined | WidgetFormSync["legend"],
		)
		.catch(undefined),

	12: tuple([z.boolean().optional().catch(undefined), z.string().optional().catch(undefined)])
		.transform(([enabled, label]) => partial({ enabled, label }) satisfies undefined | WidgetFormSync["yAxis"])
		.catch(undefined),

	13: tuple([
		z.string().optional().catch(undefined),
		z
			.array(
				tuple([z.string().optional().catch(undefined), z.string().optional().catch(undefined)])
					.transform(
						([color, value]) =>
							partial({ color, value }) satisfies undefined | Partial<GetImpulseFormInput<WidgetThresholdsStep>>,
					)
					.catch(undefined),
			)
			.optional()
			.catch(undefined),
	])
		.transform(
			([mode, steps]) => partial({ mode, steps }) satisfies undefined | Partial<WidgetFormSync["thresholds"], "deep">,
		)
		.catch(undefined),
})
	.transform(
		(sync) =>
			partial({
				kind: sync[0],
				unit: sync[1],
				decimals: sync[2],
				connectNulls: sync[3],
				min: sync[4],
				max: sync[5],
				metricLabel: sync[6],
				fontSize: sync[7],
				sparkline: sync[8],
				calculation: sync[9],
				color: sync[10],
				legend: sync[11],
				yAxis: sync[12],
				thresholds: sync[13],
			}) satisfies undefined | Partial<WidgetFormSync, "deep">,
	)
	.catch(undefined);

function encoder(settings: z.infer<typeof decoder>): Encoder {
	return Encoder.shape({
		0: Encoder.primitive(ALL_KINDS.indexOf(settings?.kind ?? TIME_SERIES_CHART), ALL_KINDS.indexOf(TIME_SERIES_CHART)),

		1: Encoder.primitive(settings?.unit, WidgetUnit.default),
		2: Encoder.primitive(settings?.decimals, WidgetDecimals.default),
		3: Encoder.primitive(settings?.connectNulls, WidgetConnectNulls.default),
		4: Encoder.primitive(settings?.min, WidgetRange.default),
		5: Encoder.primitive(settings?.max, WidgetRange.default),
		6: Encoder.primitive(settings?.metricLabel, WidgetMetricLabel.default),
		7: Encoder.primitive(settings?.fontSize, WidgetFontSize.default),
		8: Encoder.primitive(settings?.sparkline, WidgetSparkline.default),
		9: Encoder.primitive(settings?.calculation, WidgetCalculation.default),
		10: Encoder.primitive(settings?.color, WidgetColor.default),

		11: Encoder.tuple(
			Encoder.primitive(settings?.legend?.enabled, WidgetLegend.default.enabled),
			Encoder.primitive(settings?.legend?.position, WidgetLegend.default.element.position),
			Encoder.primitive(settings?.legend?.mode, WidgetLegend.default.element.mode.active),

			Encoder.list(
				// the legend table columns definition is order agnostic
				sortBy(settings?.legend?.table ?? WidgetLegend.default.element.mode.branches.table, identity),
				sortBy(WidgetLegend.default.element.mode.branches.table, identity),
				(calculation) => Encoder.primitive(calculation),
			),
		),

		12: Encoder.tuple(
			Encoder.primitive(settings?.yAxis?.enabled, WidgetYAxis.default.enabled),
			Encoder.primitive(settings?.yAxis?.label, WidgetYAxis.default.element.label),
		),

		13: Encoder.tuple(
			Encoder.primitive(settings?.thresholds?.mode, WidgetThresholdsMode.default),
			Encoder.list(settings?.thresholds?.steps, [], (threshold) =>
				Encoder.tuple(
					Encoder.primitive(threshold?.color, WidgetThresholdsStep.default.color),
					Encoder.primitive(threshold?.value, WidgetThresholdsStep.default.value),
				),
			),
		),
	});
}

const FormState = { decoder, encoder };

export { FormState };
